import React, { useState, useEffect } from 'react';
import { configAPI } from '../services/api';
import './EmailAccounts.css';

const EMPTY_FORM = {
  name: '',
  fromName: '',
  fromEmail: '',
  host: '',
  port: 587,
  secure: false,
  username: '',
  password: '',
  signatureId: null
};

// Yaygın sağlayıcıların SMTP ayarları
const PRESETS = [
  { label: 'Gmail / Google Workspace', host: 'smtp.gmail.com', port: 587, secure: false, hint: 'Google hesabınızda 2 adımlı doğrulamayı açıp "Uygulama şifresi" oluşturun; normal şifre çalışmaz.' },
  { label: 'Outlook / Microsoft 365', host: 'smtp.office365.com', port: 587, secure: false, hint: 'Microsoft 365 yöneticinizin SMTP AUTH izni vermesi gerekebilir.' },
  { label: 'Yandex', host: 'smtp.yandex.com', port: 465, secure: true, hint: 'Yandex ayarlarından uygulama şifresi oluşturun.' },
  { label: 'Hostinger', host: 'smtp.hostinger.com', port: 465, secure: true, hint: 'E-posta hesabınızın şifresini kullanın.' }
];

const errorText = (error, fallback) => error.response?.data?.message || fallback;

function EmailAccounts() {
  const [accounts, setAccounts] = useState([]);
  // null: form kapalı, 'new': yeni hesap, sayı: düzenlenen hesap
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [hasPassword, setHasPassword] = useState(false);
  const [presetHint, setPresetHint] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [testing, setTesting] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [signatures, setSignatures] = useState([]);

  const load = () => configAPI.getEmailAccounts()
    .then((response) => setAccounts(response.data.data))
    .catch(() => {});

  // İmzalar aynı sayfada düzenlenebildiği için form açılırken de yenilenir
  const loadSignatures = () => configAPI.getSignatures()
    .then((response) => setSignatures(response.data.data))
    .catch(() => {});

  useEffect(() => {
    load();
    loadSignatures();
  }, []);

  const defaultSignature = signatures.find((sig) => sig.isDefault);
  const signatureLabel = (account) => {
    const linked = signatures.find((sig) => sig.id === account.signatureId);
    if (linked) return linked.name;
    return defaultSignature ? `Varsayılan (${defaultSignature.name})` : 'İmza yok';
  };

  const update = (patch) => setForm((current) => ({ ...current, ...patch }));

  const openNew = () => {
    loadSignatures();
    setEditing('new');
    setForm(EMPTY_FORM);
    setHasPassword(false);
    setPresetHint('');
    setFeedback(null);
  };

  const openEdit = (account) => {
    loadSignatures();
    setEditing(account.id);
    setForm({ ...EMPTY_FORM, ...account, password: '' });
    setHasPassword(account.hasPassword);
    setPresetHint('');
    setFeedback(null);
  };

  const applyPreset = (preset) => {
    update({ host: preset.host, port: preset.port, secure: preset.secure });
    setPresetHint(preset.hint);
  };

  // Port değişince SSL'i uygun hale getir (465 → SSL, 587 → STARTTLS)
  const changePort = (value) => {
    const port = parseInt(value, 10) || '';
    update({ port, ...(port === 465 ? { secure: true } : port === 587 ? { secure: false } : {}) });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      const response = editing === 'new'
        ? await configAPI.createEmailAccount(form)
        : await configAPI.updateEmailAccount(editing, form);
      await load();
      setEditing(null);
      setFeedback({ type: 'success', text: response.data.message });
    } catch (error) {
      setFeedback({ type: 'error', text: errorText(error, 'Hesap kaydedilemedi') });
    } finally {
      setSaving(false);
    }
  };

  const handleDefault = async (account) => {
    try {
      const response = await configAPI.setDefaultEmailAccount(account.id);
      setAccounts(response.data.data);
      setFeedback({ type: 'success', text: response.data.message });
    } catch (error) {
      setFeedback({ type: 'error', text: errorText(error, 'Varsayılan yapılamadı') });
    }
  };

  const handleDelete = async (account) => {
    if (!window.confirm(`"${account.name}" hesabı silinsin mi? Bu hesabı kullanan kampanyalar varsayılan hesaba geçer.`)) return;
    try {
      const response = await configAPI.deleteEmailAccount(account.id);
      setAccounts(response.data.data);
      if (editing === account.id) setEditing(null);
      setFeedback({ type: 'success', text: 'Hesap silindi' });
    } catch (error) {
      setFeedback({ type: 'error', text: errorText(error, 'Hesap silinemedi') });
    }
  };

  const handleTest = async (account) => {
    setFeedback(null);
    try {
      const response = await configAPI.testEmailAccount(account.id, testTo);
      setFeedback({ type: 'success', text: response.data.message });
      setTesting(null);
    } catch (error) {
      setFeedback({ type: 'error', text: errorText(error, 'Test e-postası gönderilemedi') });
    }
  };

  return (
    <div className="settings-section email-accounts">
      <div className="ea-header">
        <div>
          <h2>Gönderen E-posta Hesapları</h2>
          <p className="help-text">
            Kampanyaların gönderileceği SMTP hesapları. Birden fazla hesap ekleyip kampanya oluştururken
            hangisinden gönderileceğini seçebilirsiniz. Kaydederken bağlantı ve giriş bilgileri test edilir.
          </p>
        </div>
        {editing === null && (
          <button type="button" className="btn-primary" onClick={openNew}>+ Hesap ekle</button>
        )}
      </div>

      {feedback && <div className={`ea-feedback ${feedback.type}`}>{feedback.text}</div>}

      {accounts.length === 0 && editing === null && (
        <div className="ea-empty">Henüz gönderen hesabı eklenmedi. E-posta kampanyası göndermek için en az bir hesap ekleyin.</div>
      )}

      {accounts.length > 0 && (
        <div className="ea-list">
          {accounts.map((account) => (
            <div key={account.id} className={`ea-row ${editing === account.id ? 'editing' : ''}`}>
              <div className="ea-info">
                <div className="ea-name">
                  {account.name}
                  {account.isDefault && <span className="ea-default">Varsayılan</span>}
                </div>
                <div className="ea-meta">
                  {account.fromName ? `${account.fromName} · ` : ''}{account.fromEmail || account.username}
                  <span> · {account.host}:{account.port}{account.secure ? ' (SSL)' : ''}</span>
                </div>
                <div className="ea-signature">İmza: <strong>{signatureLabel(account)}</strong></div>
                {testing === account.id && (
                  <div className="ea-test">
                    <input
                      type="email"
                      value={testTo}
                      onChange={(e) => setTestTo(e.target.value)}
                      placeholder="Test e-postasının gideceği adres"
                    />
                    <button type="button" className="btn-outline" onClick={() => handleTest(account)} disabled={!testTo}>Gönder</button>
                    <button type="button" className="link-btn muted-link" onClick={() => setTesting(null)}>Vazgeç</button>
                  </div>
                )}
              </div>
              <div className="ea-actions">
                <button type="button" className="btn-outline" onClick={() => { setTesting(account.id); setTestTo(''); }}>Test e-postası</button>
                <button type="button" className="btn-outline" onClick={() => openEdit(account)}>Düzenle</button>
                {!account.isDefault && (
                  <button type="button" className="btn-outline" onClick={() => handleDefault(account)}>Varsayılan yap</button>
                )}
                <button type="button" className="btn-outline danger" onClick={() => handleDelete(account)}>Sil</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <form className="ea-form" onSubmit={handleSave}>
          <h3>{editing === 'new' ? 'Yeni gönderen hesabı' : 'Hesabı düzenle'}</h3>

          <div className="ea-presets">
            <span>Hazır ayar:</span>
            {PRESETS.map((preset) => (
              <button type="button" key={preset.label} onClick={() => applyPreset(preset)}>{preset.label}</button>
            ))}
          </div>
          {presetHint && <small className="ea-hint">{presetHint}</small>}

          <div className="ea-grid">
            <div className="form-group">
              <label>Hesap adı</label>
              <input type="text" value={form.name} onChange={(e) => update({ name: e.target.value })} placeholder="Ör. Satış ekibi" />
              <small>Sadece listede görünür. Boş bırakılırsa adres kullanılır.</small>
            </div>
            <div className="form-group">
              <label>Gönderen adı</label>
              <input type="text" value={form.fromName} onChange={(e) => update({ fromName: e.target.value })} placeholder="Ör. AR Yazılım Satış" />
              <small>Alıcının gelen kutusunda görünen ad. Boşsa imzadaki şirket adı kullanılır.</small>
            </div>
            <div className="form-group">
              <label>SMTP sunucusu</label>
              <input type="text" value={form.host} onChange={(e) => update({ host: e.target.value })} placeholder="smtp.firma.com" required />
            </div>
            <div className="form-group ea-port">
              <label>Port</label>
              <div className="ea-port-row">
                <input type="number" value={form.port} onChange={(e) => changePort(e.target.value)} required />
                <label className="ea-check">
                  <input type="checkbox" checked={form.secure} onChange={(e) => update({ secure: e.target.checked })} />
                  SSL/TLS
                </label>
              </div>
              <small>587: SSL kapalı (STARTTLS) · 465: SSL açık</small>
            </div>
            <div className="form-group">
              <label>Kullanıcı adı</label>
              <input type="text" value={form.username} onChange={(e) => update({ username: e.target.value })} placeholder="info@firma.com" required autoComplete="off" />
            </div>
            <div className="form-group">
              <label>Şifre</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => update({ password: e.target.value })}
                placeholder={hasPassword ? 'Kayıtlı — değiştirmek için yazın' : '••••••••'}
                required={!hasPassword}
                autoComplete="new-password"
              />
            </div>
            <div className="form-group">
              <label>Bu hesabın imzası</label>
              <select
                value={form.signatureId || ''}
                onChange={(e) => update({ signatureId: Number(e.target.value) || null })}
              >
                <option value="">
                  {defaultSignature ? `Varsayılan imza (${defaultSignature.name})` : 'Varsayılan imza'}
                </option>
                {signatures.map((sig) => (
                  <option key={sig.id} value={sig.id}>{sig.name}</option>
                ))}
              </select>
              <small>Bu hesaptan gönderilen kampanyalarda ve test e-postalarında otomatik kullanılır.</small>
            </div>
            <div className="form-group">
              <label>Gönderen adresi (isteğe bağlı)</label>
              <input type="email" value={form.fromEmail} onChange={(e) => update({ fromEmail: e.target.value })} placeholder="Boşsa kullanıcı adı" />
              <small>Sunucunuz farklı bir adresten göndermeye izin veriyorsa doldurun.</small>
            </div>
          </div>

          <div className="ea-form-actions">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Bağlantı test ediliyor…' : editing === 'new' ? 'Test et ve kaydet' : 'Kaydet'}
            </button>
            <button type="button" className="btn-outline" onClick={() => setEditing(null)} disabled={saving}>İptal</button>
          </div>
        </form>
      )}
    </div>
  );
}

export default EmailAccounts;
